/**
 * NavigationButtons Component
 * Extracted from DeviceSpecifications.tsx
 * Handles wizard navigation buttons (Previous, Next)
 */

import React from 'react';
import styled from 'styled-components';

// Styled Components
const NavigationButtonsContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 0.875rem;
  margin-top: 0.75rem;
  width: 100%;

  @media (max-width: 640px) {
    gap: 0.5rem;
    margin-top: 0.75rem;
  }
`;

const NavButton = styled.button.withConfig({
  shouldForwardProp: (prop) => !['variant', 'isLoading'].includes(prop),
})<{ variant?: 'primary' | 'secondary'; isLoading?: boolean }>`
  padding: 0.6rem 1rem;
  border-radius: 9999px;
  font-size: 0.9rem;
  font-weight: 700;
  cursor: ${props => props.isLoading ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  border: 1px solid;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  position: relative;
  opacity: ${props => props.isLoading ? 0.6 : 1};
  pointer-events: ${props => props.isLoading ? 'none' : 'auto'};

  ${props => props.variant === 'primary' ? `
    background: #1f2937;
    color: #ffffff;
    border-color: #1f2937;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);

    &:hover {
      background: #374151;
      border-color: #4b5563;
      transform: translateY(-1px);
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.22);
    }

    /* right arrow */
    &::after {
      content: '';
      display: inline-block;
      width: 16px;
      height: 16px;
      margin-left: 0.45rem;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-size: 16px 16px;
      transform: translateX(0);
      transition: transform 0.2s ease;
    }
    &:hover::after { transform: translateX(4px); }
  ` : `
    background: #ffffff;
    color: #374151;
    border: 1.5px solid #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);

    &:hover {
      background: #f9fafb;
      border-color: #d1d5db;
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    }

    /* Left arrow pseudo for back */
    &::before {
      content: '';
      display: inline-block;
      width: 16px;
      height: 16px;
      margin-right: 0.45rem;
      background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23374151' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='15 18 9 12 15 6'%3E%3C/polyline%3E%3C/svg%3E");
      background-repeat: no-repeat;
      background-size: 16px 16px;
      transform: translateX(0);
      transition: transform 0.2s ease;
    }
    &:hover::before { transform: translateX(-4px); }
  `}

  @media (max-width: 640px) {
    flex: 1;
    padding: 0.75rem 1rem;
    border-radius: 12px;
    font-size: 0.9rem;
    font-weight: 600;

    &:hover {
      transform: none;
    }

    &::before, &::after {
      width: 14px;
      height: 14px;
      background-size: 14px 14px;
    }
  }

  .dark & {
    ${props => props.variant === 'primary' ? `
      background: #e2e8f0;
      border-color: #e2e8f0;
      color: #0f172a;

      &:hover {
        background: #f1f5f9;
        border-color: #f1f5f9;
      }

      &::after {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%230f172a' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='9 18 15 12 9 6'%3E%3C/polyline%3E%3C/svg%3E");
      }
    ` : `
      background: transparent;
      border: 1.5px solid #334155;
      color: #cbd5e1;

      &:hover {
        background: rgba(255, 255, 255, 0.05);
        border-color: #475569;
      }

      &::before {
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23cbd5e1' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='15 18 9 12 15 6'%3E%3C/polyline%3E%3C/svg%3E");
      }
    `}
  }
`;

// Component Props
export interface NavigationButtonsProps {
  canGoBack: boolean;
  canGoNext: boolean;
  onBack: () => void;
  onNext: () => void;
  t: (key: string) => string;
  isLoading?: boolean;
}

// Main Component
const NavigationButtons: React.FC<NavigationButtonsProps> = ({
  canGoBack,
  canGoNext,
  onBack,
  onNext,
  t,
  isLoading = false,
}) => {
  return (
    <NavigationButtonsContainer>
      {canGoBack ? (
        <NavButton variant="secondary" onClick={onBack} aria-label={t('back')}>
          <span>{t('back')}</span>
        </NavButton>
      ) : <div style={{ flex: 1 }} />}
      {canGoNext && (
        <NavButton
          variant="primary"
          onClick={onNext}
          aria-label={isLoading ? t('calculating') || 'Hesaplanıyor...' : t('volgende')}
          disabled={isLoading}
          isLoading={isLoading}
        >
          <span>{isLoading ? (t('calculating') || 'Hesaplanıyor...') : t('volgende')}</span>
        </NavButton>
      )}
    </NavigationButtonsContainer>
  );
};

export default NavigationButtons;

